/**
 * Chemotherapy Service
 * 
 * Provides API methods for chemotherapy management including
 * regimen templates, treatment cycles, and toxicity tracking.
 */

import { getAuth } from 'firebase/auth';

const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';

/**
 * Get authorization headers for API requests
 */
const getAuthHeaders = async () => {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }
  const token = await user.getIdToken();
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

/**
 * Handle API response
 */
const handleResponse = async (response) => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || error.message || 'API request failed');
  }
  return response.json();
};

// =============================================================================
// CHEMOTHERAPY REGIMENS
// =============================================================================

/**
 * Get chemotherapy regimen templates
 * @param {string} facilityId - Facility ID
 * @param {Object} options - Query options
 * @param {string} [options.cancerType] - Filter by cancer type
 * @param {boolean} [options.isActive=true] - Filter by active status
 * @returns {Promise<{data: Array}>}
 */
export const getChemotherapyRegimens = async (facilityId, options = {}) => {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams({
    facilityId,
    isActive: options.isActive !== false ? 'true' : 'false'
  });
  if (options.cancerType) params.append('cancerType', options.cancerType);

  const response = await fetch(`${API_BASE}/api/oncology/regimens?${params}`, {
    method: 'GET',
    headers
  });
  return handleResponse(response);
};

/**
 * Create a chemotherapy regimen template
 * @param {Object} regimenData - Regimen data
 * @param {string} regimenData.facilityId - Facility ID
 * @param {string} regimenData.name - Regimen name
 * @param {string} regimenData.code - Regimen code (e.g., 'FOLFOX', 'R-CHOP')
 * @param {Array} regimenData.drugs - Array of drug definitions
 * @param {number} regimenData.cycleLengthDays - Cycle length in days
 * @param {number} regimenData.numberOfCycles - Standard number of cycles
 * @param {Array} regimenData.cancerTypes - Applicable cancer types
 * @returns {Promise<Object>}
 */
export const createChemotherapyRegimen = async (regimenData) => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/api/oncology/regimens`, {
    method: 'POST',
    headers,
    body: JSON.stringify(regimenData)
  });
  return handleResponse(response);
};

// =============================================================================
// CHEMOTHERAPY CYCLES
// =============================================================================

/**
 * Get chemotherapy cycles for a patient
 * @param {string} patientId - Patient ID
 * @param {string} facilityId - Facility ID
 * @param {Object} options - Query options
 * @param {string} [options.treatmentPlanId] - Filter by treatment plan
 * @param {string} [options.encounterId] - Filter by encounter (to get chemo cycles for a specific visit)
 * @param {string} [options.status] - Filter by status
 * @param {number} [options.page=1] - Page number
 * @param {number} [options.limit=20] - Items per page
 * @returns {Promise<{data: Array, total: number, page: number, limit: number, hasMore: boolean}>}
 */
export const getChemotherapyCycles = async (patientId, facilityId, options = {}) => {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams({
    facilityId,
    page: options.page || 1,
    limit: options.limit || 20
  });
  if (options.treatmentPlanId) params.append('treatmentPlanId', options.treatmentPlanId);
  if (options.encounterId) params.append('encounterId', options.encounterId);
  if (options.status) params.append('status', options.status);

  const response = await fetch(`${API_BASE}/api/oncology/patients/${patientId}/chemo-cycles?${params}`, {
    method: 'GET',
    headers
  });
  return handleResponse(response);
};

/**
 * Create a chemotherapy cycle
 * @param {Object} cycleData - Cycle data
 * @param {string} cycleData.facilityId - Facility ID
 * @param {string} cycleData.patientId - Patient ID
 * @param {string} cycleData.treatmentPlanId - Treatment plan ID
 * @param {string} cycleData.regimenId - Regimen template ID
 * @param {number} cycleData.cycleNumber - Cycle number
 * @param {number} cycleData.totalPlannedCycles - Total planned cycles
 * @param {string} cycleData.plannedStartDate - Planned start date
 * @param {number} [cycleData.bsaValue] - BSA value for dose calculation
 * @param {string} [cycleData.encounterId] - Encounter ID for this infusion visit
 * @returns {Promise<Object>}
 */
export const createChemotherapyCycle = async (cycleData) => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/api/oncology/chemo-cycles`, {
    method: 'POST',
    headers,
    body: JSON.stringify(cycleData)
  });
  return handleResponse(response);
};

/**
 * Update a chemotherapy cycle
 * @param {string} cycleId - Cycle ID
 * @param {Object} updateData - Fields to update
 * @returns {Promise<Object>}
 */
export const updateChemotherapyCycle = async (cycleId, updateData) => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/api/oncology/chemo-cycles/${cycleId}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(updateData)
  });
  return handleResponse(response);
};

/**
 * Record drug administration for a cycle
 * @param {string} cycleId - Cycle ID
 * @param {Object} administrationData - Drug administration details
 * @returns {Promise<Object>}
 */
export const recordDrugAdministration = async (cycleId, administrationData) => {
  const headers = await getAuthHeaders();
  
  // First get the current cycle
  const response = await fetch(`${API_BASE}/api/oncology/chemo-cycles/${cycleId}`, {
    method: 'GET',
    headers
  });
  const cycle = await handleResponse(response);
  
  // Add the new administration to the existing array
  const drugsAdministered = [...(cycle.drugsAdministered || []), administrationData];
  
  // Update the cycle
  return updateChemotherapyCycle(cycleId, {
    drugsAdministered,
    status: 'In Progress',
    actualStartDate: cycle.actualStartDate || new Date().toISOString()
  });
};

/**
 * Apply dose modification to a cycle
 * @param {string} cycleId - Cycle ID
 * @param {Object} modification - Dose modification details
 * @param {string} modification.reason - Reason for modification
 * @param {number} modification.percentReduction - Reduction percentage
 * @param {Array} modification.drugsAffected - Drugs affected by modification
 * @param {string} modification.approvedBy - Approving physician
 * @returns {Promise<Object>}
 */
export const applyDoseModification = async (cycleId, modification) => {
  return updateChemotherapyCycle(cycleId, {
    doseModification: modification,
    status: 'Dose Reduced'
  });
};

/**
 * Record cycle delay
 * @param {string} cycleId - Cycle ID
 * @param {Object} delayInfo - Delay details
 * @param {number} delayInfo.daysDelayed - Number of days delayed
 * @param {string} delayInfo.reason - Reason for delay
 * @param {string} delayInfo.approvedBy - Approving physician
 * @param {string} delayInfo.newStartDate - New planned start date
 * @returns {Promise<Object>}
 */
export const recordCycleDelay = async (cycleId, delayInfo) => {
  return updateChemotherapyCycle(cycleId, {
    delay: {
      daysDelayed: delayInfo.daysDelayed,
      reason: delayInfo.reason,
      approvedBy: delayInfo.approvedBy
    },
    plannedStartDate: delayInfo.newStartDate,
    status: 'Delayed'
  });
};

/**
 * Complete a chemotherapy cycle
 * @param {string} cycleId - Cycle ID
 * @param {Object} completionData - Completion details
 * @param {string} [completionData.responseAssessment] - Response assessment (CR, PR, SD, PD)
 * @param {string} [completionData.responseNotes] - Response notes
 * @returns {Promise<Object>}
 */
export const completeChemotherapyCycle = async (cycleId, completionData = {}) => {
  return updateChemotherapyCycle(cycleId, {
    ...completionData,
    status: 'Completed',
    actualEndDate: new Date().toISOString()
  });
};

// =============================================================================
// TOXICITY RECORDS
// =============================================================================

/**
 * Get toxicity records for a patient
 * @param {string} patientId - Patient ID
 * @param {string} facilityId - Facility ID
 * @param {Object} options - Query options
 * @param {string} [options.encounterId] - Filter by encounter (to get toxicities recorded during a specific visit)
 * @param {string} [options.grade] - Filter by CTCAE grade
 * @param {string} [options.category] - Filter by toxicity category
 * @param {number} [options.page=1] - Page number
 * @param {number} [options.limit=20] - Items per page
 * @returns {Promise<{data: Array, total: number, page: number, limit: number, hasMore: boolean}>}
 */
export const getToxicityRecords = async (patientId, facilityId, options = {}) => {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams({
    facilityId,
    page: options.page || 1,
    limit: options.limit || 20
  });
  if (options.encounterId) params.append('encounterId', options.encounterId);
  if (options.grade) params.append('grade', options.grade);
  if (options.category) params.append('category', options.category);

  const response = await fetch(`${API_BASE}/api/oncology/patients/${patientId}/toxicity?${params}`, {
    method: 'GET',
    headers
  });
  return handleResponse(response);
};

/**
 * Get all toxicity records for a facility (facility-wide list view)
 * @param {string} facilityId - Facility ID
 * @param {Object} options - Query options
 * @param {string} [options.search] - Search term for patient name or adverse event
 * @param {string} [options.grade] - Filter by CTCAE grade
 * @param {string} [options.category] - Filter by toxicity category
 * @param {number} [options.page=1] - Page number
 * @param {number} [options.limit=20] - Items per page
 * @returns {Promise<{data: Array, total: number, page: number, limit: number, hasMore: boolean}>}
 */
export const getAllToxicityRecords = async (facilityId, options = {}) => {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams({
    facilityId,
    page: options.page || 1,
    limit: options.limit || 20
  });
  if (options.search) params.append('search', options.search);
  if (options.grade) params.append('grade', options.grade);
  if (options.category) params.append('category', options.category);

  const response = await fetch(`${API_BASE}/api/oncology/toxicity?${params}`, {
    method: 'GET',
    headers
  });
  return handleResponse(response);
};

/**
 * Get a single toxicity record by ID
 * @param {string} recordId - Toxicity record ID
 * @returns {Promise<Object>}
 */
export const getToxicityRecord = async (recordId) => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/api/oncology/toxicity/${recordId}`, {
    method: 'GET',
    headers
  });
  return handleResponse(response);
};

/**
 * Create a toxicity record
 * @param {Object} toxicityData - Toxicity record data
 * @param {string} toxicityData.facilityId - Facility ID
 * @param {string} toxicityData.patientId - Patient ID
 * @param {string} toxicityData.adverseEvent - Adverse event type
 * @param {string} toxicityData.category - Toxicity category
 * @param {string} toxicityData.ctcaeGrade - CTCAE grade (1-5)
 * @param {string} [toxicityData.onsetDate] - Onset date
 * @param {string} [toxicityData.chemoCycleId] - Associated chemo cycle ID
 * @param {string} [toxicityData.encounterId] - Encounter ID where toxicity was documented
 * @returns {Promise<Object>}
 */
export const createToxicityRecord = async (toxicityData) => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/api/oncology/toxicity`, {
    method: 'POST',
    headers,
    body: JSON.stringify(toxicityData)
  });
  return handleResponse(response);
};

/**
 * Update a toxicity record
 * @param {string} recordId - Toxicity record ID
 * @param {Object} updateData - Fields to update
 * @returns {Promise<Object>}
 */
export const updateToxicityRecord = async (recordId, updateData) => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/api/oncology/toxicity/${recordId}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(updateData)
  });
  return handleResponse(response);
};

/**
 * Resolve a toxicity event
 * @param {string} recordId - Toxicity record ID
 * @param {string} resolvedDate - Resolution date
 * @param {string} [outcomeDescription] - Outcome description
 * @returns {Promise<Object>}
 */
export const resolveToxicity = async (recordId, resolvedDate, outcomeDescription) => {
  return updateToxicityRecord(recordId, {
    resolvedDate,
    outcomeDescription,
    duration: Math.ceil((new Date(resolvedDate) - new Date()) / (1000 * 60 * 60 * 24))
  });
};

const chemotherapyService = {
  // Regimens
  getChemotherapyRegimens,
  createChemotherapyRegimen,
  // Cycles
  getChemotherapyCycles,
  createChemotherapyCycle,
  updateChemotherapyCycle,
  recordDrugAdministration,
  applyDoseModification,
  recordCycleDelay,
  completeChemotherapyCycle,
  // Toxicity
  getToxicityRecords,
  getAllToxicityRecords,
  getToxicityRecord,
  createToxicityRecord,
  updateToxicityRecord,
  resolveToxicity
};

export default chemotherapyService;

